import { breadcrumbSchema } from "@/lib/schema";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { SiteLayout } from "@/components/site/Layout";
import { VisibilityAuditWidget } from "@/components/site/VisibilityAuditWidget";
import { trackFormView } from "@/lib/analytics";
import { VISIBILITY_AUDIT_WIDGET_ID } from "@/lib/forms";

const FAQS = [
  {
    q: "What does the Visibility Audit actually include?",
    a: "Your audit scores you across five dimensions that decide whether buyers and sellers find you: Google Business Profile, AI Search Citability (ChatGPT, Gemini, Perplexity), Directory Consistency, Reviews & Reputation, and Owned Content / Website Performance. You'll see exactly where you rank, where you're invisible, and what's quietly costing you business.",
  },
  {
    q: "How long does it take?",
    a: "About 60 seconds. Enter your name, business, phone, email, and website - your score is generated instantly. No call required, no obligation, just the data.",
  },
  {
    q: "Is it really free? What's the catch?",
    a: "It's completely free and there's no catch. We believe the first step to wealth and freedom is an honest look at where you stand. If the report opens your eyes, you can decide whether you want to talk with us about fixing it.",
  },
  {
    q: "How is my score calculated?",
    a: "Each of the five dimensions is equally weighted and graded on a 0-100% scale (Worse, Bad, Average, Better, Exceptional). Reviews factor in your positive ratio, platform weighting (Google 75% / Facebook 25%), and review volume reliability. Listings check NAP consistency across major directories. Business Details look at chat widget, text-enabled number, and review reply engagement.",
  },
  {
    q: "What happens after I submit?",
    a: "Your report is generated and delivered immediately - no waiting, no scheduled call. You'll see your scores section by section with clear explanations of what each grade means. From there, it's your decision what to do next: fix it yourself, or request a Growth Strategy Session with our team to map a plan.",
  },
  {
    q: "Will someone call me right away?",
    a: "No. There is no automatic call or hard sell. You get the report. If you want a conversation about what to do with it, you book that on your own terms.",
  },
];

export const Route = createFileRoute("/visibility-audit")({
  head: () => ({
    meta: [
      { title: "Free Real Estate Agent SEO and Visibility Audit" },
      {
        name: "description",
        content:
          "Get a free real estate agent SEO and real estate agent branding audit. See how buyers, sellers, and AI find you today, and what invisibility costs you.",
      },
      { property: "og:title", content: "Free Visibility Audit - Kristan Cole Network" },
      {
        property: "og:description",
        content:
          "See exactly how buyers, sellers, and AI find you today. Free instant report.",
      },
    ],
    links: [
      { rel: "canonical", href: "https://joinkcn.com/visibility-audit" },
      { rel: "preconnect", href: "https://services.leadconnectorhq.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://services.leadconnectorhq.com" },
    ],
    scripts: [
      breadcrumbSchema([{ name: "Visibility Audit", path: "/visibility-audit" }]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: VisibilityAuditPage,
});

function VisibilityAuditPage() {
  useEffect(() => {
    trackFormView("visibility_audit", "/visibility-audit");
  }, []);
  return (
    <SiteLayout>
      <section className="bg-background">
        <div className="container-edge pt-32 md:pt-36 pb-4">
          <p className="eyebrow text-foreground/70">Free Visibility Audit</p>
        </div>
        <div className="container-edge">
          <div className="grid lg:grid-cols-12 gap-0">
            {/* Framing copy */}
            <div className="lg:col-span-5 bg-black text-white pr-0 md:pr-8 lg:pr-12 pt-4 pb-16 lg:pt-6 lg:pb-24">
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                Stop renting attention. <span className="serif-italic text-white/90">Start owning it.</span>
              </h1>
              <p className="font-display italic text-2xl md:text-3xl mt-5 text-white/90 leading-snug">
                See how buyers, sellers, and AI find you.
              </p>
              <p className="mt-3 text-white/75 text-base md:text-lg leading-relaxed">
                Leads you buy disappear. Visibility you build compounds.
              </p>
              <div className="mt-8 space-y-5 text-white/85 text-base md:text-lg leading-relaxed">
                <p>
                  Paid ads and bought leads are rent. The moment you stop paying,
                  the pipeline stops - and every month you start over at zero.
                </p>
                <p>
                  Organic visibility is an asset. A profile, a post, a strong
                  presence in Google and AI search keeps working for months or
                  years after you make it. That's the difference between an
                  expense and equity.
                </p>
                <p className="font-display italic text-white/90">
                  Your first step to wealth and freedom is an honest look at what you own.
                </p>
              </div>
            </div>

            {/* Audit widget */}
            <div className="lg:col-span-7 bg-black lg:pl-12 pt-4 pb-16 lg:pt-6 lg:pb-24">
              <VisibilityAuditWidget />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background">

        <div className="container-edge py-12 md:py-14">
          <p className="eyebrow text-foreground/70 mb-6">Rent vs. Own</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl mb-16">
            An expense, or <span className="serif-italic text-primary">equity?</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-background/40 p-10 md:p-12">
              <p className="eyebrow text-foreground/60 mb-6">Rented attention</p>
              <h3 className="font-display text-3xl mb-6">Paid ads &amp; bought leads</h3>
              <ul className="space-y-4 text-foreground/85 leading-relaxed">
                <li> -  Stops the moment you stop paying</li>
                <li> -  Every month restarts at zero</li>
                <li> -  Feast or famine. Burnout baked in.</li>
                <li> -  An expense on the P&amp;L. Nothing left behind.</li>
              </ul>
            </div>
            <div className="bg-background/40 p-10 md:p-12 border-l-2 border-primary">
              <p className="eyebrow text-primary mb-6">Owned visibility</p>
              <h3 className="font-display text-3xl mb-6">Organic presence &amp; AI search</h3>
              <ul className="space-y-4 text-foreground/85 leading-relaxed">
                <li> -  Keeps working months and years later</li>
                <li> -  Compounds with every post, profile, review</li>
                <li> -  Consistent pipeline. Calmer business.</li>
                <li> -  Equity on the balance sheet. A real asset.</li>
              </ul>
            </div>
          </div>
          <p className="mt-10 max-w-2xl text-foreground/80 text-lg leading-relaxed font-display italic">
            The audit shows you what you actually own today - and where you're
            still paying rent.
          </p>
        </div>
      </section>

      <section className="bg-background">
        <div className="container-edge py-12 md:py-14">
          <p className="eyebrow text-foreground/70 mb-6">Questions, answered</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl mb-12">
            Before you take the look.
          </h2>
          <div className="divide-y divide-foreground/10">
            {FAQS.map((f) => (
              <details key={f.q} className="py-6 group">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <h3 className="font-display text-xl md:text-2xl pr-4">{f.q}</h3>
                  <span className="text-primary text-2xl leading-none mt-1 group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-foreground/85 leading-relaxed max-w-3xl">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
